import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Sidebar from "../Sidebar";
import TopPanel from "../TopPanel";
import Footer from "../Footer";
import {
  getAllCampaigns,
  getProducts,
  updateCampaign,
} from "./campaignService.js";

const GREEN = "#3b5e40";
const CTL_H = 56; // same control height as the create form
const lbl = { fontSize: 14, fontWeight: 600, marginBottom: 4 };
const cardStyle = { borderRadius: 10, border: "1px solid #e0e0e0" };
const hintStyle = { fontSize: 11, display: "block", marginTop: 4, lineHeight: 1.3 };
const TYPE_LABEL = {
  coupon: "Coupon / Discount",
  offer: "Offer",
  wallet_cashback: "Wallet Cashback",
  referral: "Refer & Earn",
};
const BADGE = {
  coupon: ["#e6f0e9", "#2c5a31"],
  offer: ["#e6f2ff", "#0056b3"],
  wallet_cashback: ["#faebd7", "#a0522d"],
  referral: ["#f3e8ff", "#6b2fa0"],
};
const NAME_LABEL = { coupon: "Coupon name", offer: "Offer name", referral: "Program name" };

// ---------- helpers (same logic as AllCampaigns) ----------
const toJsDate = (ts) => (ts?.toDate ? ts.toDate() : ts ? new Date(ts) : null);
// createCampaign saves new Date("YYYY-MM-DD") (UTC midnight), so read it back in UTC
const toInputDate = (ts) => {
  const d = toJsDate(ts);
  return d && !isNaN(d) ? d.toISOString().slice(0, 10) : "";
};
const pid = (p) => p.id || p.productId;
const pname = (p) => p.name || p.productName || "";
const optionsOf = (p) => {
  if (p.packagingOptions?.length) return p.packagingOptions;
  if (p.qty != null || p.unit) return [{ packaging: p.qty, pkgUnit: p.unit }];
  if (p.pkgUnit) return [{ packaging: "", pkgUnit: p.pkgUnit }];
  return [{}];
};
const pkgText = (o) => [o.packaging, o.pkgUnit].filter(Boolean).join(" ");
const buildOptions = (products) =>
  products.flatMap((p) =>
    optionsOf(p).map((o, i) => ({
      key: `${pid(p)}::${i}`,
      productId: pid(p),
      name: pname(p),
      pkg: pkgText(o),
      qty: Number(o.packaging) || null,
      unit: o.pkgUnit || "",
      label: [pname(p), pkgText(o)].filter(Boolean).join(" "),
    }))
  );
const parseKey = (key) => (key || "").split("::")[0];
const calcTotal = (count, opt) => {
  const n = Number(count);
  if (!n) return 0;
  return opt?.qty ? Math.round(n * opt.qty * 1000) / 1000 : n;
};
const totalLabel = (count, opt) => {
  const t = calcTotal(count, opt);
  return t ? `${t}${opt?.unit ? ` ${opt.unit}` : ""}` : "—";
};
// saved productId + packaging text  ->  dropdown option key
const findKey = (opts, productId, packaging) => {
  const same = opts.filter((o) => o.productId === productId);
  return (same.find((o) => o.pkg === packaging) || same[0])?.key || "";
};
// saved packs (or total / pack size)  ->  "number of packs" input value
const packsFrom = (packs, total, opt) => {
  if (packs) return String(packs);
  if (total && opt?.qty) return String(Math.round((total / opt.qty) * 1000) / 1000);
  return String(total || 1);
};
const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : "—");
const withCurrent = (list, current) =>
  current !== "" && current != null && !list.map(String).includes(String(current)) ? [...list, current] : list;

// ---------- small components (outside the page so inputs keep focus) ----------
const Seg = ({ value, options, onChange }) => (
  <div className="btn-group w-100" style={{ flexWrap: "nowrap" }}>
    {options.map(([v, label]) => (
      <button key={v} type="button" className="btn" onClick={() => onChange(v)}
        style={{
          flex: "1 1 0", minWidth: 0, height: CTL_H, padding: "0 0.5rem", fontSize: 15,
          display: "flex", alignItems: "center", justifyContent: "center",
          background: value === v ? GREEN : "#fff", color: value === v ? "#fff" : "#6c757d",
          borderColor: value === v ? GREEN : "#ced4da", boxShadow: "none", whiteSpace: "nowrap",
        }}>
        {label}
      </button>
    ))}
  </div>
);
const Field = ({ label, col = 4, hint, children }) => (
  <div className={`col-12 col-md-${col} mb-3`}>
    <label style={{ ...lbl, display: "block", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", minHeight: 21, marginBottom: 6 }}>{label}</label>
    {children}
    {hint}
  </div>
);
const Section = ({ title, children }) => (
  <div className="card shadow-sm mb-4" style={cardStyle}>
    <div className="card-body">
      <h6 style={{ fontWeight: "bold", marginBottom: 15, fontSize: 18 }}>{title}</h6>
      {children}
    </div>
  </div>
);
const Block = ({ title, subtitle, tint, children }) => (
  <div className="mb-3" style={{ border: "1px solid #e0e0e0", borderRadius: 10, padding: "16px 16px 4px", backgroundColor: tint || "#fff" }}>
    <div className="mb-3">
      <div style={{ fontWeight: 700, fontSize: 15, color: "#2c2c2c" }}>{title}</div>
      {subtitle && <div className="text-muted" style={{ fontSize: 12 }}>{subtitle}</div>}
    </div>
    {children}
  </div>
);
const TypeBadge = ({ type }) => {
  const badge = BADGE[type] || ["#eee", "#333"];
  return (
    <span style={{ background: badge[0], color: badge[1], padding: "4px 10px", borderRadius: 15, fontSize: 12, fontWeight: 800 }}>
      {TYPE_LABEL[type] || type || "Unknown"}
    </span>
  );
};
const PackQty = ({ count, opt, onCount }) => (
  <>
    <div className="input-group flex-nowrap">
      <input type="number" min="1" step="1" className="form-control" style={{ flex: "1 1 auto", minWidth: 0 }} value={count} onChange={(e) => onCount(e.target.value)} />
      <span className="input-group-text" style={{ whiteSpace: "nowrap" }}>{opt ? `× ${opt.pkg || "pack"}` : "× pack"}</span>
    </div>
    <small className="text-muted" style={hintStyle}>
      {opt ? <>Total: <strong>{totalLabel(count, opt)}</strong></> : "Select a product first"}
    </small>
  </>
);

function EditCampaign() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState("");
  const [campaign, setCampaign] = useState(null);
  const [products, setProducts] = useState([]);
  const [form, setForm] = useState(null);


  const todayStr = new Date().toLocaleDateString("en-CA"); // YYYY-MM-DD (local time)
  const started = campaign ? toInputDate(campaign.startAt) <= todayStr : false;

  // When started, everything can change except the start date.
  const set = (k, v) =>
    setForm((f) => (started && k === "startDate" ? f : { ...f, [k]: v }));

  // ---------- load campaign + products, then pre-fill the form ----------
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [all, prods] = await Promise.all([getAllCampaigns(), getProducts().catch(() => [])]);
        const c = all.find((x) => x.id === id || x.campaignCode === id);
        if (!c) { setNotFound(true); return; }

        const opts = buildOptions(prods || []);
        const cfg = c.config || {};
        const el = c.eligibility || {};
        const productKey = findKey(opts, el.productIds?.[0], el.packaging);
        const rewardKey = findKey(opts, cfg.benefit?.productId, cfg.benefit?.packaging);
        const optByKey = (k) => opts.find((o) => o.key === k);

        setProducts(prods || []);
        setCampaign(c);
        setForm({
          name: c.name || "",
          description: c.description || "",
          // coupon
          applyMethod: { code: "customer_code", auto: "auto_apply", both: "both" }[cfg.applyMode] || "customer_code",
          supportCanApply: !!cfg.allowAgentApply,
          discountType: cfg.discountType === "percent" ? "percentage" : "fixed",
          discountAmount: cfg.discountValue ?? "",
          // eligibility
          productId: productKey,
          minDays: el.minDurationDays || 90,
          customerType: el.customerType || "all",
          acquisitionSource: el.acquisitionSources?.[0] || "",
          // offer
          qualifyQty: packsFrom(cfg.condition?.packs, cfg.condition?.value, optByKey(productKey)),
          rewardType: cfg.benefit?.kind || "free_product",
          rewardProduct: rewardKey,
          freeQty: packsFrom(cfg.benefit?.packs, cfg.benefit?.qty, optByKey(rewardKey)),
          specialPrice: cfg.benefit?.amount ?? "",
          // referral
          referrerAmount: cfg.referrerReward ?? "",
          refereeAmount: cfg.refereeReward ?? "",
          // validity
          startDate: toInputDate(c.startAt),
          endDate: toInputDate(c.endAt),
          maxUsage: c.maxUsesPerCustomer ?? 1,
        });
      } catch (e) {
        console.error(e);
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  const wrap = (content) => (
    <>
      {(loading || saving) && (
        <div className="loader-overlay"><div><img style={{ height: "6rem" }} src="images/loader.gif" alt="loader" /></div></div>
      )}
      <div className="container-scroller" style={{ backgroundColor: "#f9f7f2" }}>
        <TopPanel />
        <div className="container-fluid page-body-wrapper">
          <Sidebar />
          <div className="main-panel" style={{ backgroundColor: "#f9f7f2", minHeight: "100vh" }}>
            <div className="content-wrapper" style={{ padding: "2rem" }}>{content}</div>
            <Footer />
          </div>
        </div>
      </div>
    </>
  );

  if (!campaign || !form) {
    return wrap(
      <div>
        <Link to="/all-campaigns" style={{ color: GREEN, fontWeight: 600 }}>← All Campaigns</Link>
        {!loading && notFound && <h4 className="mt-4 text-muted">Campaign not found.</h4>}
      </div>
    );
  }

  // ---------- derived ----------
  const c = campaign;
  const cfg = c.config || {};
  const type = c.type;
  const detailsPath = `/coupons-discount-details/${c.id}`;
  const opts = buildOptions(products);
  const optOf = (key) => opts.find((o) => o.key === key);
  const selectedOpt = optOf(form.productId);
  const rewardOpt = optOf(form.rewardProduct);
  const isFree = form.rewardType === "free_product";

  // ---------- save ----------
  const validate = () => {
    const f = form;

    if (!f.name.trim()) return `${NAME_LABEL[type] || "Name"} is required.`;
    if (type !== "referral" && !f.productId) return "Select an eligible product.";
    if (type === "coupon" && !(Number(f.discountAmount) > 0)) return "Enter a discount amount.";
    if (type === "coupon" && f.discountType === "percentage" && Number(f.discountAmount) > 100) return "Percentage cannot exceed 100.";
    if (type === "offer" && !(Number(f.qualifyQty) > 0)) return "Enter the required quantity.";
    if (type === "offer" && isFree && !f.rewardProduct) return "Select a free product.";
    if (type === "offer" && isFree && !(Number(f.freeQty) > 0)) return "Enter the free quantity.";
    if (type === "offer" && !isFree && !(Number(f.specialPrice) > 0)) return "Enter a special price.";
    if (type === "referral" && !(Number(f.referrerAmount) > 0 && Number(f.refereeAmount) > 0)) return "Enter both reward amounts.";
    if (!f.startDate || !f.endDate) return "Select a start and end date.";
    if (f.endDate < f.startDate) return "End date cannot be before the start date.";
    return "";
  };

  const buildPayload = () => {
    const f = form;

    const total = (count, key) => calcTotal(count, optOf(key));
    const eligibility = {
      ...(c.eligibility || {}),
      productIds: f.productId ? [parseKey(f.productId)] : [],
      packaging: f.productId ? optOf(f.productId)?.pkg || null : null,
      unit: f.productId ? optOf(f.productId)?.unit || "" : "",
      minDurationDays: Number(f.minDays),
      customerType: f.customerType,
      acquisitionSources: f.acquisitionSource ? [f.acquisitionSource] : [],
    };

    let config = { ...cfg };
    if (type === "coupon") {
      const isFixed = f.discountType !== "percentage";
      config = {
        ...cfg, // code stays as it is
        applyMode: { customer_code: "code", auto_apply: "auto", both: "both" }[f.applyMethod] || "code",
        allowAgentApply: f.supportCanApply,
        discountType: isFixed ? "fixed" : "percent",
        discountValue: Number(f.discountAmount),
        // discountAmount & finalPrice campaignService.updateCampaign mein calculate hote hain
      };
    } else if (type === "offer") {
      const condOpt = optOf(f.productId);
      const rewardOptSel = optOf(f.rewardProduct);
      config = {
        ...cfg,
        condition: {
          ...(cfg.condition || {}),
          kind: cfg.condition?.kind || "quantity",
          productId: parseKey(f.productId),
          productIds: [parseKey(f.productId)],
          packaging: condOpt?.pkg || null,
          value: total(f.qualifyQty, f.productId),
          unit: condOpt?.unit || "",
          packs: Number(f.qualifyQty),
        },
        benefit: isFree
          ? {
              kind: "free_product",
              productId: parseKey(f.rewardProduct),
              productIds: [parseKey(f.rewardProduct)],
              packaging: rewardOptSel?.pkg || null,
              qty: total(f.freeQty, f.rewardProduct),
              unit: rewardOptSel?.unit || "",
              packs: Number(f.freeQty),
              amount: null,
            }
          : {
              kind: "special_price",
              productId: parseKey(f.productId),
              productIds: [parseKey(f.productId)],
              packaging: condOpt?.pkg || null,
              qty: null,
              amount: Number(f.specialPrice),
              // discountAmount & finalPrice service mein calculate honge
            },
      };
    } else if (type === "referral") {
      config = { ...cfg, referrerReward: Number(f.referrerAmount), refereeReward: Number(f.refereeAmount) };
    }

    return {
      name: f.name.trim(),
      description: f.description.trim(),
      startDate: f.startDate, // service converts these to Timestamps
      endDate: f.endDate,
      maxUsesPerCustomer: Number(f.maxUsage) || null,
      eligibility,
      config,
    };
  };

  const handleSave = async () => {
    const msg = validate();
    if (msg) { setError(msg); window.scrollTo({ top: 0, behavior: "smooth" }); return; }
    setError("");
    setSaving(true);
    try {
      await updateCampaign(c.id, buildPayload());
      navigate(detailsPath);
    } catch (e) {
      console.error(e);
      setError("Could not save changes: " + e.message);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } finally {
      setSaving(false);
    }
  };

  // ---------- reusable pieces ----------
  const productSelect = (value, onChange) => (
    <select className="form-select" value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">{products.length ? "Select product" : "Loading products..."}</option>
      {opts.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
    </select>
  );
  const durationField = (col = 4) => (
    <Field label={type === "referral" ? "Referee must subscribe for" : "Minimum duration"} col={col}>
      <select className="form-select" value={form.minDays} onChange={(e) => set("minDays", e.target.value)}>
        {withCurrent([30, 90, 180], form.minDays).map((d) => (
          <option key={d} value={d}>{{ 30: "1 month", 90: "3 months", 180: "6 months" }[d] || `${d} days`}</option>
        ))}
      </select>
    </Field>
  );
  const customerFields = (typeCol = 4, srcCol = 4) => (
    <>
      <Field label="Customer type" col={typeCol}>
        <Seg value={form.customerType} onChange={(v) => set("customerType", v)} options={[["new", "New"], ["existing", "Existing"], ["all", "All"]]} />
      </Field>
      <Field label="Acquisition source" col={srcCol}>
        <select className="form-select" value={form.acquisitionSource} onChange={(e) => set("acquisitionSource", e.target.value)}>
          <option value="">Any source</option>
          {form.acquisitionSource && <option value={form.acquisitionSource}>{form.acquisitionSource}</option>}
        </select>
      </Field>
    </>
  );

  const applyMethodLabel = { customer_code: "Customer code", auto_apply: "Auto-apply", both: "Customer code + Auto-apply" };
  const summaryRows = {
    coupon: [
      ["Applied by", `${applyMethodLabel[form.applyMethod]}${form.supportCanApply ? " · support" : ""}`],
      ["Code", cfg.code || c.code || "—"],
      ["Product", selectedOpt?.label || "—"], ["Min duration", `${form.minDays} days`], ["Customers", cap(form.customerType)],
      ["Discount", form.discountAmount ? (form.discountType === "percentage" ? `${form.discountAmount}%` : `₹${form.discountAmount}`) : "—"],
      ["Per customer", `${form.maxUsage} time`],
    ],
    offer: [
      ["Product", selectedOpt?.label || "—"],
      ["Required qty", form.qualifyQty ? totalLabel(form.qualifyQty, selectedOpt) : "—"],
      ["Min duration", `${form.minDays} days`], ["Customers", cap(form.customerType)],
      ["Reward", isFree ? `${form.freeQty ? totalLabel(form.freeQty, rewardOpt) : "—"} · ${rewardOpt?.label || "—"}` : form.specialPrice ? `₹${form.specialPrice} special price` : "—"],
      ["Per customer", `${form.maxUsage} time`],
    ],
    referral: [
      ["Qualifies after", `${form.minDays} days`], ["Referrer earns", form.referrerAmount ? `₹${form.referrerAmount}` : "—"],
      ["Referee earns", form.refereeAmount ? `₹${form.refereeAmount}` : "—"], ["Per customer", `${form.maxUsage} time`],
    ],
  }[type] || [];

  return wrap(
    <div className="cc-form">
      <style>{`
        .cc-form input.form-control,
        .cc-form select.form-select,
        .cc-form .input-group-text { height: ${CTL_H}px; }
        .cc-form select.form-select { padding-right: 2.25rem; }
      `}</style>

      <div className="mb-4">
        <Link to={detailsPath} style={{ color: GREEN, fontWeight: 600, fontSize: 16 }}>← Back to campaign</Link>
        <h2 className="mt-2" style={{ fontWeight: 700, fontFamily: "serif", color: "#2c2c2c", fontSize: 32 }}>
          {type === "referral" ? "Edit Programme" : "Edit Campaign"}
        </h2>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      {started && (
        <div className="alert alert-warning">
          This campaign has already started. The start date cannot be changed.
        </div>
      )}

      <div className="row">
        <div className="col-12 col-lg-8">
          <div>
            <Section title="1 · Campaign">
              <div className="row">
                <Field label="Type" col={4}>
                  <div style={{ height: CTL_H, display: "flex", alignItems: "center" }}><TypeBadge type={type} /></div>
                </Field>
                <Field label="Internal Campaign ID" col={4}>
                  <input className="form-control" value={c.campaignCode || c.id} readOnly style={{ backgroundColor: "#f1f3f5" }} />
                </Field>
                <Field label="Code" col={4} hint={<small className="text-muted" style={hintStyle}>Code cannot be changed after creation</small>}>
                  <input className="form-control" value={cfg.code || c.code || ""} readOnly style={{ backgroundColor: "#f1f3f5" }} />
                </Field>
              </div>
            </Section>

            <Section title="2 · Basics">
              <Field label={NAME_LABEL[type] || "Name"} col={12}>
                <input className="form-control" value={form.name} onChange={(e) => set("name", e.target.value)} />
              </Field>
              <label style={lbl}>Description</label>
              <textarea className="form-control" rows={3} value={form.description} onChange={(e) => set("description", e.target.value)} />
            </Section>

            <Section title={type === "referral" ? "3 · Qualification & rewards" : "3 · Eligibility & " + (type === "offer" ? "reward" : "discount")}>
              {/* ---------- COUPON ---------- */}
              {type === "coupon" && (
                <>
                  <label style={lbl}>How is it applied?</label>
                  <div className="d-flex gap-3 mb-3 flex-wrap align-items-center" style={{ fontSize: 13 }}>
                    {[["customer_code", "Customer enters code"], ["auto_apply", "Auto-apply when eligible"], ["both", "Both (code + auto-apply)"]].map(([v, t]) => (
                      <div key={v} onClick={() => set("applyMethod", v)}
                        style={{ border: "1px solid #ccc", borderRadius: 8, padding: "10px 15px", cursor: "pointer", backgroundColor: form.applyMethod === v ? "#e8f5e9" : "white" }}>
                        <input type="radio" readOnly checked={form.applyMethod === v} className="me-2" />{t}
                      </div>
                    ))}
                    <div>
                      <input type="checkbox" id="sup" checked={form.supportCanApply} onChange={(e) => set("supportCanApply", e.target.checked)} className="me-2" />
                      <label htmlFor="sup" style={{ cursor: "pointer" }}>Support team can also apply it</label>
                    </div>
                  </div>
                  <div className="row">
                    <Field label="Eligible product" col={6}>{productSelect(form.productId, (v) => set("productId", v))}</Field>
                    {durationField(6)}
                  </div>
                  <div className="row">
                    <Field label="Discount type">
                      <Seg value={form.discountType} onChange={(v) => set("discountType", v)} options={[["fixed", "Fixed ₹"], ["percentage", "Percentage %"]]} />
                    </Field>
                    <Field label="Discount amount">
                      <div className="input-group flex-nowrap">
                        <input type="number" min="0" className="form-control" style={{ minWidth: 0 }} value={form.discountAmount} onChange={(e) => set("discountAmount", e.target.value)} />
                        <span className="input-group-text">{form.discountType === "percentage" ? "%" : "₹"}</span>
                      </div>
                    </Field>
                  </div>
                  <div className="row">{customerFields(4, 8)}</div>
                </>
              )}

              {/* ---------- OFFER ---------- */}
              {type === "offer" && (
                <>
                  <Block title="When does the offer apply?" subtitle="The customer must meet this condition">
                    <div className="row">
                      <Field label="Eligible product" col={6}>{productSelect(form.productId, (v) => set("productId", v))}</Field>
                      <Field label="Required quantity" col={6}>
                        <PackQty count={form.qualifyQty} opt={selectedOpt} onCount={(v) => set("qualifyQty", v)} />
                      </Field>
                    </div>
                    <div className="row">
                      {durationField(4)}
                      {customerFields(4, 4)}
                    </div>
                  </Block>
                  <Block title="What does the customer get?" subtitle="Reward given once the condition is met" tint="#f9fcf9">
                    <div className="row">
                      <Field label="Reward type" col={4}>
                        <Seg value={form.rewardType} onChange={(v) => set("rewardType", v)} options={[["free_product", "Free product"], ["special_price", "Special price"]]} />
                      </Field>
                      {isFree ? (
                        <>
                          <Field label="Free product" col={4}>{productSelect(form.rewardProduct, (v) => set("rewardProduct", v))}</Field>
                          <Field label="Free quantity" col={4}>
                            <PackQty count={form.freeQty} opt={rewardOpt} onCount={(v) => set("freeQty", v)} />
                          </Field>
                        </>
                      ) : (
                        <Field label="Special price (₹)" col={4}>
                          <div className="input-group flex-nowrap">
                            <span className="input-group-text">₹</span>
                            <input type="number" min="0" className="form-control" style={{ minWidth: 0 }} value={form.specialPrice} onChange={(e) => set("specialPrice", e.target.value)} />
                          </div>
                        </Field>
                      )}
                    </div>
                  </Block>
                </>
              )}

              {/* ---------- REFERRAL ---------- */}
              {type === "referral" && (
                <div className="row">
                  {durationField()}
                  <Field label="Referrer earns (₹)">
                    <input type="number" min="0" className="form-control" value={form.referrerAmount} onChange={(e) => set("referrerAmount", e.target.value)} />
                  </Field>
                  <Field label="Referee earns (₹)">
                    <input type="number" min="0" className="form-control" value={form.refereeAmount} onChange={(e) => set("refereeAmount", e.target.value)} />
                  </Field>
                </div>
              )}

              {type === "wallet_cashback" && (
                <div className="text-muted">Wallet cashback settings are not editable yet.</div>
              )}
            </Section>
          </div>
          <Section title="4 · Validity & limits">
            <div className="row">
              <Field label="Start date">
                <input type="date" className="form-control" disabled={started}
                  value={form.startDate} onChange={(e) => set("startDate", e.target.value)} />
              </Field>
              <Field label="End date">
                <input type="date" className="form-control" min={form.startDate}
                  value={form.endDate} onChange={(e) => set("endDate", e.target.value)} />
              </Field>
              <Field label="Max usage per customer">
                <select className="form-select"
                  value={form.maxUsage} onChange={(e) => set("maxUsage", e.target.value)}>
                  {withCurrent([1, 2, 3], form.maxUsage).map((n) => (
                    <option key={n} value={n}>{n === 1 ? "1 time (recommended)" : `${n} times`}</option>
                  ))}
                </select>
              </Field>
            </div>
          </Section>
        </div>

        {/* Right summary */}
        <div className="col-12 col-lg-4">
          <div className="card shadow-sm position-sticky" style={{ ...cardStyle, top: 20 }}>
            <div className="card-body">
              <p className="text-muted mb-2" style={{ fontSize: 13, fontWeight: "bold" }}>SUMMARY</p>
              <TypeBadge type={type} />
              <h4 className="mt-3" style={{ fontWeight: "bold", fontFamily: "serif", fontSize: 22 }}>{form.name || "Untitled campaign"}</h4>
              <div className="mt-3 mb-4" style={{ fontSize: 14 }}>
                {summaryRows.map(([k, v]) => (
                  <div key={k} className="d-flex justify-content-between py-2 border-bottom">
                    <span className="text-muted">{k}</span><span style={{ fontWeight: 600, textAlign: "right" }}>{v}</span>
                  </div>
                ))}
              </div>
              <p className="text-muted" style={{ fontSize: 12 }}>Changes apply to future redemptions. Past usage and stats stay the same.</p>
              <button className="btn w-100 mb-2" disabled={saving} onClick={handleSave}
                style={{ backgroundColor: GREEN, color: "#fff", fontWeight: "bold", padding: 12, borderRadius: 8 }}>
                {saving ? "Saving..." : "Save changes"}
              </button>
              <Link to={detailsPath} className="btn w-100" style={{ fontWeight: "bold", padding: 12, borderRadius: 8, border: "1px solid #ccc" }}>
                Cancel
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default EditCampaign;